import { useEffect, useRef, useState } from 'react';
import './adjust-infographic.scss';
import { useNavigate } from "react-router-dom";
import PreviewInfographicComponent from '../preview-infographic/preview-infographic.component';
import ImageBoxInfographicComponent from './image-box-infographic/image-box-infographic.component';
import TextBoxInfographicComponent from './text-box-infographic/text-box-infographic.component';
import { sizeOptions, infographicTemplateConfig } from '../../../config/infographic.config';
import { InfographicSize } from '../../../model/infographic-size.enum';
import { InfographicTextBox } from '../../../model/infographic-text-box.model';
import { InfographicImageBox } from '../../../model/infographic-image-box.model';
import { TextType } from '../../../model/text-type.enum';
import ICON_AI from "../../../assets/images/icon-ai.png";
import Button from '@mui/material/Button';
import InfographicSummarizeFormModal from '../../../modal/infographic-summarize-form/infographic-summarize-form';
import { downloadElementAsImage, getElementAsBlob, optimizeImage } from '../../../utility/download-image.utility';
import InfographicService from '../../../services/infographic.service';
import { InfographicContent } from '../../../model/infographic-content.model';
import { Infographic } from '../../../model/infographic.model';
import CircularProgress from '@mui/material/CircularProgress';
import { storage } from '../../../firebase/auth.firebase';
import { ref, uploadBytesResumable } from "firebase/storage";
import LoadingModal from '../../../modal/loading/loading';
import { downloadFile } from '../../../utility/download-file.utility';
import { TemplateConfig } from '../../../model/template-config.model';
import { useSnackbar } from '../../../utility/snackbar.utility';
import CustomSnackbar from '../../snackbar/snackbar.component';
import { addLog } from '../../../utility/add-log.utility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const AdjustInfographicComponent = ({ mediaId, infoId }) => {

    const navigate = useNavigate();
    const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

    const captureRef = useRef(null);
    const referenceDivRef = useRef<HTMLDivElement | null>(null);

    const [template, setTemplate] = useState<any>();
    const [selectedTemplate, setSelectedTemplate] = useState<number>(1);
    const [selectedSize, setSelectedSize] = useState<InfographicSize>(InfographicSize.RATIO_16_9);
    const [textBoxes, setTextBoxes] = useState<InfographicTextBox[]>([]);
    const [imageBoxes, setImageBoxes] = useState<InfographicImageBox[]>([]);
    const [width, setWidth] = useState<number>(0);
    const [height, setHeight] = useState<number>(0);
    const [previewScale, setPreviewScale] = useState<number>(1);
    const [realScale, setRealScale] = useState<number>(1);
    const [isShowSummarizeFormModal, setIsShowSummarizeFormModal] = useState(false);
    const [isShowLoadingModal, setIsShowLoadingModal] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [infographic, setInfographic] = useState<Infographic>();
    const [spellcheckActiveId, setSpellcheckActiveId] = useState<number | null>(null);
    const [isSpellcheckLoading, setIsSpellcheckLoading] = useState(false);
    const [hasInitialized, setHasInitialized] = useState(false);

    useEffect(() => {
        fetchInfo();
    }, []);

    useEffect(() => {
        if (referenceDivRef.current) {
            updatePreviewScale();
        }
    }, [referenceDivRef.current, selectedSize, width, height]);

    useEffect(() => {
        if (previewScale && selectedSize) {
            updateRealScale();
        }
    }, [previewScale, selectedSize]);

    useEffect(() => {
        if (!hasInitialized && infographic && previewScale !== 1) {
            initializeBoxes();
            setHasInitialized(true);
        }
    }, [infographic, previewScale, hasInitialized]);

    const fetchInfo = async () => {
        try {
            setIsLoading(true);
            const response = await InfographicService.getInfoById(mediaId, infoId);

            const data: Infographic = response.data;
            const templateId = data.template_config.template_id;
            const templateSize = data.template_config.size;

            setInfographic(data);
            setTemplate(infographicTemplateConfig[templateId][templateSize]);
            setSelectedTemplate(templateId);
            setSelectedSize(templateSize);

            initializeDimensions(templateSize);
        } catch (error) {
            setIsLoading(false);
            showSnackbar('', "error");
        } finally {
            setIsLoading(false);
        }
    }

    const initializeBoxes = async () => {
        if (!infographic) return;

        // Initialize text boxes
        if (Array.isArray(infographic.text_config) && infographic.text_config.length > 0) {
            const tempData = infographic.text_config.map(item => ({ ...item, text: scaleFontSizeToPreview(item.text) }))
            setTextBoxes(tempData);
        } else if (infographic.infographic) {
            generateTextBoxesFromData(infographic.template_config, infographic.infographic, infographic.infographic_quote || '', infographic.infographic_title || '');
        }

        // Initialize image boxes
        const templateId = infographic.template_config.template_id;
        const templateSize = infographic.template_config.size;
        const { imageBox } = infographicTemplateConfig[templateId][templateSize];
        const rawImages = infographic.raw_images ?? [];
        const tempImageBoxes = Array.isArray(infographic.raw_images)
            ? await Promise.all(
                imageBox.map(async (box) => {
                    const fileName = rawImages.find(item => Number(item.split('.')[0]) === box.id);

                    if (fileName) {
                        const url = await downloadFile(infoId, fileName, 'raw');
                        return { id: box.id, file: null, src: url };
                    }
                    return { id: box.id, file: null, src: '', fileName: '' };
                })
            )
            : imageBox.map((box) => ({ id: box.id, file: null, src: '' }));

        setImageBoxes(tempImageBoxes);
    };

    const generateTextBoxesFromData = (
        templateConfig: TemplateConfig,
        infographic: InfographicContent[],
        infoQuote: string,
        infoTitle: string
    ) => {
        const textBoxConfig = infographicTemplateConfig[templateConfig.template_id][templateConfig.size].textBox;

        const getTextContent = (box: any): string => {
            const fontSize = Math.round(box.fontSize * previewScale);
            const wrapText = (text: string) => `<span style='font-size:${fontSize}px'>${text}</span>`;

            if (box.id === 1) return wrapText(infoTitle);
            if (box.type === TextType.TOPIC) return wrapText(infographic[box.order - 1]?.topic || '');
            if (box.type === TextType.QUOTE) return wrapText(infoQuote);
            return wrapText(infographic[box.order - 1]?.content || '');
        };

        const tempTextBoxes = textBoxConfig.map((box) => ({
            id: box.id,
            text: getTextContent(box),
        }));

        setTextBoxes(tempTextBoxes);
    };

    const initializeDimensions = (selectedSize: InfographicSize) => {
        const { width: newWidth, height: newHeight } = sizeOptions[selectedSize];
        setWidth(newWidth);
        setHeight(newHeight);
    };

    const updatePreviewScale = () => {
        if (referenceDivRef.current) {
            const rect = referenceDivRef.current.getBoundingClientRect();
            const widthMinus = (selectedSize === InfographicSize.RATIO_1_1) ? 80 : 32;
            const referenceWidthNoPadding = rect.width - widthMinus;
            const referenceHeightNoPadding = window.innerHeight - 170;

            const scaleW = referenceWidthNoPadding / width;
            const scaleH = referenceHeightNoPadding / height;
            const scale = Math.min(scaleW, scaleH);

            setPreviewScale(scale);
        }
    };

    const updateRealScale = () => {
        const realSizeOption = sizeOptions[selectedSize];
        const previewW = width * previewScale;
        const scaleW = previewW / realSizeOption.width;
        setRealScale(scaleW);
    }

    const handleSaveData = async () => {
        if (!infographic) return;

        setIsShowLoadingModal(true);
        const FILE_STORAGE = `infographic_images/${infoId}/`;
        await addLog("INFOGRAPHIC_LOG", { data: { log: 'handleSaveData' }, infoId: infoId });

        try {
            const thumbnailImage = await createImageBlob("thumbnail", "jpeg");

            const fullImage = await createImageBlob("full", "png");
            await addLog("INFOGRAPHIC_LOG", { data: { log: 'fullImage', file_size: fullImage.size }, infoId: infoId });

            const [imageThumbnail, imageFull, imageRaw] = await Promise.all([
                uploadFile(`${FILE_STORAGE}thumbnail/`, "thumbnail-image.jpg", thumbnailImage),
                uploadFile(`${FILE_STORAGE}full/`, "full-image.png", fullImage),
                uploadRawFiles(`${FILE_STORAGE}raw/`)
            ]);
            await addLog("INFOGRAPHIC_LOG", { data: { log: 'uploadFiles' }, infoId: infoId });

            const rawImages = mergeRawImages(infographic.raw_images ?? [], imageRaw);

            await updateInfographicData(imageFull, imageThumbnail, rawImages);
            await addLog("INFOGRAPHIC_LOG", { data: { log: 'api update info' }, infoId: infoId });

            await downloadInfographicImage();
            await addLog("INFOGRAPHIC_LOG", { data: { log: 'downloadElementAsImage' }, infoId: infoId });

            setIsShowLoadingModal(false);
            navigate(`/detail/${mediaId}`);
        } catch (error: any) {
            await handleError(error);
        }
    };

    const scaleFontSize = (htmlContent: string, scale: number, mode: 'preview' | 'real'): string => {
        if (!htmlContent) return '';

        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, "text/html");

        const spans = doc.querySelectorAll<HTMLElement>('[style*="font-size"]');

        spans.forEach((span) => {
            const style = span.style.fontSize;
            if (style) {
                const currentSize = parseFloat(style);
                const unit = style.replace(currentSize.toString(), '').trim();

                const scaledSize = mode === 'preview'
                    ? Math.round(currentSize * scale)
                    : +(currentSize / scale).toFixed(2);

                span.style.fontSize = `${scaledSize}${unit}`;
            }
        });

        return doc.body.innerHTML;
    };

    const scaleFontSizeToPreview = (htmlContent: string): string => {
        return scaleFontSize(htmlContent, previewScale, 'preview');
    };

    const scaleFontSizeToReal = (htmlContent: string): string => {
        return scaleFontSize(htmlContent, realScale, 'real');
    };

    const createImageBlob = async (type: string, format: 'jpeg' | 'png') => {
        const { width, height, scale } = sizeOptions[selectedSize];

        return await getElementAsBlob(
            infoId,
            captureRef.current,
            width,
            height,
            format,
            type === "thumbnail" ? scale : undefined
        );
    };

    const mergeRawImages = (existingRawImages: string[], newRawImages: string[]) => {
        return Array.from(new Set([...existingRawImages, ...newRawImages]));
    };

    const updateInfographicData = async (imageFull: any, imageThumbnail: any, rawImages: string[]) => {
        if (!infographic) return;
        const tempTextBoxes = textBoxes.map(item => ({ ...item, text: scaleFontSizeToReal(item.text) }));

        await InfographicService.updateInfo(
            mediaId,
            infoId,
            infographic.template_config,
            rawImages,
            imageFull,
            imageThumbnail,
            tempTextBoxes
        );
    };

    const downloadInfographicImage = async () => {
        if (!infographic) return;
        const { width, height } = sizeOptions[selectedSize];
        await downloadElementAsImage(captureRef.current, width, height, infographic.template_config.title);
    };

    const handleError = async (error: any) => {
        const errorData = {
            code: error.code || '',
            message: error.message || '',
            name: error.name || '',
        };
        await addLog("SAVE_INFO_ERROR", { infoId: infoId, error: errorData });
        setIsShowLoadingModal(false);
        showSnackbar('', "error");
    };

    const uploadFile = (filePath: string, fileName: string, file: Blob): Promise<string> => {
        return new Promise((resolve, reject) => {
            const storageRef = ref(storage, `${filePath}${fileName}`);
            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on(
                "state_changed",
                null,
                (error) => reject(error),
                () => resolve(fileName)
            );
        });
    };

    const uploadRawFiles = (filePath: string): Promise<string[]> => {
        const uploadPromises = imageBoxes
            .filter((item) => item.file)
            .map((item) => {
                const file = item.file!;

                return optimizeImage(file).then((optimizedBlob) => {
                    const fileName = `${item.id}.png`;

                    return uploadFile(filePath, fileName, optimizedBlob);
                });
            });

        return Promise.all(uploadPromises);
    };

    const handleSummarizeFormClose = (result) => {
        setIsShowSummarizeFormModal(false);
        if (!result) return;

        const { template_config, infoContent, infoQuote, infoTitle } = result;

        setInfographic(prev => ({
            ...prev!,
            template_config,
            infographic: infoContent,
            infographic_quote: infoQuote,
            infographic_title: infoTitle,
        }));

        generateTextBoxesFromData(template_config, infoContent, infoQuote, infoTitle);
        fetchSummary();
    }

    const fetchSummary = async () => {
        const summaryResponse = await InfographicService.getSummary(mediaId, infoId);
        if (!summaryResponse) return;

        setInfographic(prev => ({
            ...prev!,
            summary: summaryResponse.data
        }));
    }

    const handleClickCancel = () => {
        navigate(`/detail/${mediaId}`);
    }

    const handleUpdateImageBox = (updatedBox: InfographicImageBox) => {
        setImageBoxes((prev) =>
            prev.map((box) => box.id === updatedBox.id ? updatedBox : box)
        );
    };

    const handleUpdateTextBox = (updatedBox: InfographicTextBox) => {
        setTextBoxes((prev) =>
            prev.map((box) => box.id === updatedBox.id ? updatedBox : box)
        );
    }

    const handleUpdateSpellcheck = (id: number | null) => {
        setSpellcheckActiveId((prev) => (prev === id ? null : id));
    }

    const handleUpdateSpellcheckLoading = (value: boolean) => {
        setIsSpellcheckLoading(value);
    }

    const handleCopyText = () => {
        const textBoxConfig = template.textBox;
        const mergedData = textBoxes.map((item, index) => {
            const layoutItem = textBoxConfig[index];
            if (layoutItem) {
                const textWithoutHTML = item.text.replace(/<[^>]+>/g, '');

                // เพิ่มเว้น 2 บรรทัด
                const newText = ((item.id !== 1 && layoutItem.groupStart) || layoutItem.type === TextType.QUOTE)
                    ? `\n\n${textWithoutHTML}`
                    : textWithoutHTML;
                return newText;
            }
            return null;
        }).filter(item => item !== null);

        const textToCopy = mergedData.join('\n');

        navigator.clipboard.writeText(textToCopy)
            .then(() => showSnackbar('ข้อความถูกคัดลอกแล้ว'))
            .catch(() => showSnackbar('เกิดข้อผิดพลาดในการคัดลอกข้อความ', 'error'));
    };

    const handleClickSummary = () => {
        window.open(`/infographic/transcript/${mediaId}/${infoId}`, '_blank', 'noopener,noreferrer');
    }

    return (
        <>
            {
                isLoading ?
                    <CircularProgress className='mt-5' /> :
                    <div className='info-container'>
                        <div className='row'>
                            <div className='col-6'>
                                <div className='content-wrapper'>
                                    <div className='d-flex align-items-center justify-content-between mb-4 position-relative'>
                                        <div>
                                            <img src={ICON_AI} className='me-1' width={25} height={24} />
                                            <span className='title'>ปรับแต่งเนื้อหาบน Infographic</span>
                                        </div>

                                        <Button
                                            variant="outlined"
                                            className='button-summarize'
                                            startIcon={<img src={ICON_AI} className='me-1' width={20} height={19} />}
                                            onClick={() => setIsShowSummarizeFormModal(true)}
                                        >
                                            สรุปเนื้อหาใหม่อีกครั้ง
                                        </Button>

                                        <Button
                                            variant="outlined"
                                            className='button-summarize'
                                            startIcon={<img src={ICON_AI} className='me-1' width={20} height={19} />}
                                            onClick={handleClickSummary}
                                        >
                                            ดูสรุปเนื้อหาทั้งหมด
                                        </Button>
                                    </div>

                                    <div>
                                        {
                                            imageBoxes && imageBoxes.map((box, index) => {
                                                const templateImage = template.imageBox[index];

                                                return (
                                                    <ImageBoxInfographicComponent
                                                        key={`upload-image-${box.id}`}
                                                        box={box}
                                                        templateImage={templateImage}
                                                        onUpdateBox={handleUpdateImageBox}
                                                    />
                                                )
                                            })
                                        }

                                        {
                                            textBoxes && textBoxes.map((box, index) => {
                                                const templateText = template.textBox[index];

                                                return (
                                                    <TextBoxInfographicComponent
                                                        key={`text-editor-${box.id}`}
                                                        box={box}
                                                        templateText={templateText}
                                                        spellcheckActiveId={spellcheckActiveId}
                                                        isLoading={isSpellcheckLoading}
                                                        onUpdateBox={handleUpdateTextBox}
                                                        onUpdateSpellcheck={handleUpdateSpellcheck}
                                                        onUpdateSpellcheckLoading={handleUpdateSpellcheckLoading}
                                                    />
                                                )
                                            })
                                        }
                                    </div>
                                </div>
                            </div>

                            <div className='col-6'>
                                <div className='sticky-top'>
                                    <div ref={referenceDivRef} className='content-wrapper pb-4'>
                                        <div className='d-flex justify-content-between align-items-center mb-4'>
                                            <p className='title font-16'>Infographic Preview</p>

                                            <Button
                                                variant="text"
                                                className="chat-copy-button"
                                                startIcon={<ContentCopyIcon />}
                                                onClick={handleCopyText}
                                            >
                                                คัดลอกเนื้อหา
                                            </Button>
                                        </div>

                                        {/* Preview */}
                                        <div className='d-flex justify-content-center'>
                                            <PreviewInfographicComponent
                                                refProp={null}
                                                width={width * previewScale}
                                                height={height * previewScale}
                                                scale={previewScale}
                                                fontScale={1}
                                                selectedTemplate={selectedTemplate}
                                                selectedSize={selectedSize}
                                                textBoxes={textBoxes}
                                                imageBoxes={imageBoxes}
                                                preview={true}
                                            />
                                        </div>

                                        {/* Real Size */}
                                        <PreviewInfographicComponent
                                            refProp={captureRef}
                                            width={width}
                                            height={height}
                                            scale={1}
                                            fontScale={1 / previewScale}
                                            selectedTemplate={selectedTemplate}
                                            selectedSize={selectedSize}
                                            textBoxes={textBoxes}
                                            imageBoxes={imageBoxes}
                                        />
                                    </div>

                                    <div className='button-continue-wrapper'>
                                        <Button
                                            variant="outlined"
                                            className='button-cancel'
                                            onClick={handleClickCancel}
                                        >
                                            ยกเลิก
                                        </Button>

                                        <Button
                                            variant="contained"
                                            className='button-continue'
                                            onClick={handleSaveData}
                                        >
                                            บันทึก / ดาวน์โหลด
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div >
                    </div>
            }

            {
                isShowSummarizeFormModal && <InfographicSummarizeFormModal show={isShowSummarizeFormModal} onClose={handleSummarizeFormClose} mediaId={mediaId} infographic={infographic} />
            }

            {
                isShowLoadingModal && <LoadingModal show={isShowLoadingModal} onClose={() => setIsShowLoadingModal(false)} message={'ระบบกำลังทำการ generate เนื้อหา และ infographic<br/>อาจใช้เวลาสักครู่กรุณาอย่าปิดหน้าต่างนี้'} />
            }

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                duration={snackbar.duration}
                onClose={hideSnackbar}
            />
        </>
    )
}

export default AdjustInfographicComponent;